import { useNavigate } from 'react-router-dom'
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts'
import { SAMPLE_FLOOR, StackedShareBar, TargetProgressBar } from '../charts.jsx'
import { Banner, Card, ErrorState, Loading, Metric, MetricRow, SectionHeader, Table, fmtMinutes, fmtPct } from '../components.jsx'
import { RANGES, useApi, useRange } from '../hooks.js'
import PageHeader from './PageHeader.jsx'

/**
 * The fleet dashboard.
 *
 * **The headline is Fleet Availability, not OEE, and that is a deliberate refusal.** OEE is
 * Availability x Performance x Quality. Availability is real here — computed from merged per-asset downtime
 * intervals, which was the M7-02 fix after an earlier version overstated it by 13 points. Performance needs
 * actual-versus-rated production (lifts or cycles per hour) and Quality needs a reject signal; the system
 * captures neither, because a crane app knows when a crane was down and not how many ladles it moved.
 * Publishing "OEE" with those two terms silently set to 1.0 would hand a plant head a number that looks
 * comparable to an industry benchmark and is not. Every figure on this page traces to captured data.
 *
 * Layout is web-native rather than a widened phone screen: a KPI band, then trend and Pareto side by side,
 * then the bad-actor table. A supervisor scanning fourteen cranes wants rows, not fourteen cards.
 */
export default function Dashboard({ role }) {
  const nav = useNavigate()
  const { key, setKey, department, setDepartment, query } = useRange('90')

  const health = useApi(`/rcm/fleet-health${query}`)
  const trend = useApi(`/rcm/trend${query}`)
  const modes = useApi(`/rcm/failure-modes${query}`)
  const actors = useApi(`/rcm/bad-actors${query}&limit=8`)
  const planned = useApi(`/rcm/planned-work${query}`)
  const mttr = useApi(`/rcm/mttr-breakdown${query}`)
  // PM compliance, both halves. `/analytics/pm-compliance` is a snapshot of schedule state now;
  // `/analytics/pm-on-time` is the rate PRD §7 asks for, over the selected window. They answer different
  // questions and can disagree completely — a plant with nothing overdue today may have been late all quarter —
  // so the dashboard shows both rather than picking one and calling it "compliance".
  const pmSnapshot = useApi('/analytics/pm-compliance')
  const pmOnTime = useApi(`/analytics/pm-on-time${query}`)

  const h = health.data

  return (
    <>
      <PageHeader
        title="Fleet dashboard"
        subtitle={role === 'MANAGEMENT' ? 'Read-only management view' : undefined}
        range={{ key, setKey, department, setDepartment }}
      />

      {health.loading && <Loading rows={3} label="Loading fleet health" />}
      {health.error && <ErrorState error={health.error} onRetry={health.reload} />}

      {h && (
        <>
          {/*
            The headline is qualified when the data cannot support it.
            
            Found on the first run against real data: availability read 100.0% while one SAFETY failure was on
            record, because that complaint has no downtime interval to measure. Arithmetically right, and
            completely misleading — "perfect availability" and "we are not capturing downtime" must not render
            identically. The number stays; the caveat sits above it.
          */}
          {h.failuresMissingDowntime > 0 && (
            <Banner kind="warning">
              {h.failuresMissingDowntime} recorded {h.failuresMissingDowntime === 1 ? 'failure has' : 'failures have'} no
              downtime period, so availability and MTBF understate the real loss. These are usually faults
              closed without a repair duration.
            </Banner>
          )}

          {/* --- Headline band ------------------------------------------------ */}
          <MetricRow>
            <Metric
              emphasis
              label="Fleet availability"
              value={fmtPct(h.availabilityPct)}
              unit="%"
              hint={`${h.cranesInService} cranes in service`}
              kind={h.availabilityPct >= 95 ? 'success' : h.availabilityPct >= 85 ? 'warning' : 'danger'}
            />
            <Metric
              label="MTBF"
              value={fmtMinutes(h.mtbfMinutes)}
              hint="Mean time between failures"
            />
            <Metric label="MTTR" value={fmtMinutes(h.mttrMinutes)} hint="Mean time to repair" />
            <Metric label="MTTA" value={fmtMinutes(h.mttaMinutes)} hint="Mean time to acknowledge" />
            <Metric
              label="Planned work"
              value={fmtPct(h.plannedWorkPct)}
              unit="%"
              hint="Of all recorded jobs"
              kind={h.plannedWorkPct >= 70 ? 'success' : h.plannedWorkPct >= 40 ? 'warning' : 'danger'}
            />
          </MetricRow>

          {/*
            The attention band. These are counts a supervisor acts on today, so each one links straight to
            the list behind it — a number nobody can chase is a number nobody uses. Statutory overdue is
            given the safety tone because PRD §78 sets its target at zero: a crane operated past its
            examination date is a legal exposure, not a metric.
          */}
          <SectionHeader hint="Click any figure to see what is behind it">Needs attention</SectionHeader>
          <MetricRow>
            <Metric
              label="Statutory overdue"
              value={h.statutoryOverdue}
              hint={h.statutoryOverdue > 0 ? 'Target is zero' : 'All examinations current'}
              kind={h.statutoryOverdue > 0 ? 'safety' : 'success'}
              onClick={() => nav('/cranes?filter=overdue')}
            />
            <Metric
              label="Due within 30 days"
              value={h.statutoryDueSoon}
              kind={h.statutoryDueSoon > 0 ? 'warning' : undefined}
              onClick={() => nav('/cranes?filter=due-soon')}
            />
            <Metric
              label="Cranes down now"
              value={h.cranesDownNow}
              hint="Maintenance or locked out"
              kind={h.cranesDownNow > 0 ? 'warning' : 'success'}
              onClick={() => nav('/cranes?filter=down')}
            />
            <Metric
              label="Open breakdowns"
              value={h.openBreakdowns}
              hint={`${h.openComplaints} open complaints`}
              kind={h.openBreakdowns > 0 ? 'danger' : 'success'}
              onClick={() => nav('/complaints?filter=open-breakdown')}
            />
            <Metric
              label="PM overdue"
              value={h.pmOverdue}
              kind={h.pmOverdue > 0 ? 'warning' : 'success'}
              hint={pmSnapshot.data
                ? `of ${pmSnapshot.data.totalActiveSchedules} active rounds`
                : undefined}
              onClick={() => nav('/admin/pm')}
            />
            {/*
              PM compliance as PRD §7 defines it — on-time vs late over the selected window, which is a
              different thing from the overdue count beside it. The endpoint returns null rather than 0% when
              nothing measurable was completed, and `Metric` renders null as an em dash with "No data in this
              period"; 0% would read as "we were late every time" when the truth is there is nothing to judge.
            */}
            <Metric
              label="PM on time"
              value={pmOnTime.data?.onTimePct}
              unit={pmOnTime.data?.onTimePct != null ? '%' : undefined}
              kind={pmOnTimeKind(pmOnTime.data)}
              hint={pmOnTimeHint(pmOnTime.data)}
            />
            <Metric
              label="Resolved, no cause"
              value={h.resolvedWithoutFailureCode}
              hint="Caps root-cause analysis"
              kind={h.resolvedWithoutFailureCode > 0 ? 'warning' : 'success'}
              onClick={() => nav('/complaints?filter=unclassified')}
            />
            <Metric
              label="Open CAPA"
              value={h.openCapa}
              hint={h.overdueCapa > 0 ? `${h.overdueCapa} past due` : `${h.openIncidents} open incidents`}
              kind={h.overdueCapa > 0 ? 'danger' : undefined}
              onClick={() => nav('/incidents')}
            />
          </MetricRow>
        </>
      )}

      {/* --- Trend + Pareto ------------------------------------------------- */}
      <div className="grid-2" style={{ marginTop: 'var(--lg)' }}>
        <Card title="Availability and downtime" subtitle="Bucketed server-side so every view agrees">
          {trend.loading && <Loading rows={4} />}
          {trend.error && <ErrorState error={trend.error} onRetry={trend.reload} />}
          {trend.data && (trend.data.length === 0
            ? <p className="muted">No activity in this period.</p>
            : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={trend.data.map(shapeTrend)}>
                  <defs>
                    <linearGradient id="dt" x1="0" y1="0" x2="0" y2="1">
                      {/* A gradient on a chart fill, not on a data-bearing surface — rule 7 bans gradients
                          behind text and status, not area fills where the axis carries the value. */}
                      <stop offset="0%" stopColor="var(--sail-blue)" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="var(--sail-blue)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="var(--outline-variant)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="var(--on-surface-variant)" />
                  <YAxis yAxisId="a" domain={[0, 100]} tick={{ fontSize: 11 }} stroke="var(--on-surface-variant)" unit="%" width={44} />
                  <YAxis yAxisId="d" orientation="right" tick={{ fontSize: 11 }} stroke="var(--on-surface-variant)" width={52} />
                  <Tooltip content={<ChartTip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area yAxisId="d" type="monotone" dataKey="downtimeHours" name="Downtime (h)" stroke="var(--sail-blue)" fill="url(#dt)" strokeWidth={2} />
                  <Line yAxisId="a" type="monotone" dataKey="availabilityPct" name="Availability (%)" stroke="var(--success)" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ))}
        </Card>

        <Card
          title="Failure modes by downtime"
          subtitle="Ranked by downtime caused, not by how often it happened"
        >
          {modes.loading && <Loading rows={4} />}
          {modes.error && <ErrorState error={modes.error} onRetry={modes.reload} />}
          {modes.data && (modes.data.length === 0
            ? <p className="muted">No failures recorded in this period.</p>
            : (
              <>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={modes.data.slice(0, 7).map((m) => ({
                    name: m.failureMode,
                    hours: +(m.downtimeMinutes / 60).toFixed(1),
                    share: m.downtimeSharePct,
                    classified: m.failureCode !== null,
                  }))} layout="vertical" margin={{ left: 8 }}>
                    <CartesianGrid stroke="var(--outline-variant)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="var(--on-surface-variant)" unit="h" />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} stroke="var(--on-surface-variant)" />
                    <Tooltip content={<ChartTip />} />
                    <Bar dataKey="hours" name="Downtime (h)" radius={[0, 3, 3, 0]}>
                      {modes.data.slice(0, 7).map((m, i) => (
                        // Unclassified downtime is drawn in the steel tone rather than the brand blue, so it
                        // reads as "we do not know" rather than as another named cause.
                        <Cell key={i} fill={m.failureCode === null ? 'var(--steel)' : 'var(--sail-blue)'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {unclassifiedShare(modes.data) > 20 && (
                  <p className="muted">
                    {unclassifiedShare(modes.data).toFixed(0)}% of downtime has no confirmed failure code.
                    Root-cause analysis is limited until engineers select one when resolving.
                  </p>
                )}
              </>
            ))}
        </Card>
      </div>

      {/* --- Where the time goes + planned split ---------------------------- */}
      <div className="grid-2" style={{ marginTop: 'var(--md)' }}>
        <Card title="Where repair time goes" subtitle="One MTTR figure hides which of these to fix">
          {mttr.loading && <Loading rows={3} />}
          {mttr.error && <ErrorState error={mttr.error} onRetry={mttr.reload} />}
          {/*
            A stacked bar, not four tiles — DESIGN.md §11.1.

            These four numbers are parts of one total and the reader's question is "which stage dominates".
            Four tiles side by side show magnitudes but not proportion, so a repair where hold time is most of
            the clock looks exactly like one where it is a rounding error. Stacked and *in order*, because the
            stages are consecutive: response → repair → hold → verification. That sequence is the reason this
            is not a donut.

            The values stay as text in the legend (§11.2), so nothing that was readable as a tile is lost.
          */}
          {mttr.data && (
            <StackedShareBar
              title="Repair time by stage"
              hint="In the order they happen"
              unit=" min"
              sampleSize={mttr.data.sampleSize}
              sampleNote={sampleNote(mttr.data.sampleSize, 'resolved fault')}
              empty="Nothing resolved in this period"
              segments={[
                { label: 'Response', value: mttr.data.responseMinutes, token: 'process-cyan', hint: 'Until someone owned it' },
                { label: 'Repair', value: mttr.data.repairMinutes, token: 'sail-blue', hint: 'Working time' },
                { label: 'On hold', value: mttr.data.onHoldMinutes, token: 'warning', hint: 'Spares, access, vendor' },
                { label: 'Verification', value: mttr.data.verifyMinutes, token: 'steel', hint: 'Resolved to closed' },
              ]}
            />
          )}
        </Card>

        <Card title="Planned vs unplanned" subtitle="The clearest maintenance-maturity signal">
          {planned.loading && <Loading rows={3} />}
          {planned.error && <ErrorState error={planned.error} onRetry={planned.reload} />}
          {/*
            A progress bar against a target, not a donut — DESIGN.md §11.1.

            The metric is one percentage judged against a goal, and the reader's question is the distance to it.
            A two-slice donut renders "50% against a target of 80%" identically to "50% against a target of
            50%", which are completely different situations. 80% planned is the figure commonly cited as
            world-class maintenance, so it is drawn as a line on the scale rather than mentioned in a caption.

            Colour is withheld below five jobs by the shared floor: a plant that has recorded two jobs is not
            failing, it has recorded two jobs.
          */}
          {planned.data && (
            <>
              <TargetProgressBar
                title="Planned share of work"
                hint="80% is the usual world-class mark"
                value={planned.data.plannedPct}
                target={80}
                sampleSize={planned.data.plannedJobs + planned.data.unplannedJobs}
                sampleNote={sampleNote(planned.data.plannedJobs + planned.data.unplannedJobs, 'recorded job')}
                empty="No work recorded in this period"
              />
              {planned.data.plannedJobs + planned.data.unplannedJobs > 0 && (
                <>
                  <MetricRow>
                    <Metric label="Planned jobs" value={planned.data.plannedJobs} kind="success" />
                    <Metric label="Unplanned jobs" value={planned.data.unplannedJobs} kind="warning" />
                    <Metric label="Planned hours" value={hours(planned.data.plannedLabourHours)} />
                    <Metric label="Unplanned hours" value={hours(planned.data.unplannedLabourHours)} />
                  </MetricRow>
                  {/* The counts stay: §11.2 says a chart is never the only presentation of a number, and job
                      counts and labour hours disagree in a way the percentage cannot show. */}
                  <p className="muted">
                    Job counts treat a ten-minute greasing and a day&apos;s overhaul alike, so the hours are shown
                    beside them.
                  </p>
                </>
              )}
            </>
          )}
        </Card>
      </div>

      {/* --- Bad actors ---------------------------------------------------- */}
      <SectionHeader hint="The cranes consuming the most maintenance">Worst performers</SectionHeader>
      <Card>
        {actors.loading && <Loading rows={5} />}
        {actors.error && <ErrorState error={actors.error} onRetry={actors.reload} />}
        {actors.data && (
          <Table
            keyOf={(r) => r.assetId}
            onRowClick={(r) => nav(`/cranes/${r.assetId}`)}
            columns={[
              { key: 'assetCode', header: 'Crane', render: (r) => <strong>{r.assetCode}</strong> },
              { key: 'assetName', header: 'Name' },
              { key: 'department', header: 'Department' },
              { key: 'failures', header: 'Failures', numeric: true },
              { key: 'downtime', header: 'Downtime', numeric: true, render: (r) => fmtMinutes(r.downtimeMinutes) },
              { key: 'share', header: 'Share of fleet', numeric: true, render: (r) => `${r.downtimeSharePct.toFixed(1)}%` },
              { key: 'mtbf', header: 'MTBF', numeric: true, render: (r) => fmtMinutes(r.mtbfMinutes) ?? '—' },
              { key: 'labour', header: 'Labour', numeric: true, render: (r) => (Number(r.labourHours) ? `${Number(r.labourHours).toFixed(1)} h` : '—') },
              { key: 'mode', header: 'Top failure mode', render: (r) => r.topFailureMode || <span className="muted">Not classified</span> },
            ]}
            rows={actors.data}
            empty={<p className="muted">No downtime recorded in this period — nothing to rank.</p>}
          />
        )}
      </Card>
    </>
  )
}

function shapeTrend(p) {
  const d = new Date(p.at)
  return {
    label: d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' }),
    downtimeHours: +(p.downtimeMinutes / 60).toFixed(1),
    availabilityPct: +p.availabilityPct.toFixed(1),
    failures: p.failures,
  }
}

function unclassifiedShare(modes) {
  return modes.filter((m) => m.failureCode === null).reduce((a, m) => a + m.downtimeSharePct, 0)
}

function hours(v) {
  const n = Number(v)
  return n ? `${n.toFixed(1)} h` : '0'
}

/**
 * "Based on N x" — the sample size, in words, on every chart (DESIGN.md §11.3).
 *
 * Stated rather than implied because a figure from one repair and a figure from two hundred look identical on
 * a bar, and only one of them is worth acting on. Says so plainly below the floor.
 */
function sampleNote(n, noun) {
  if (!n) return undefined
  const plural = n === 1 ? noun : `${noun}s`
  return n < SAMPLE_FLOOR
    ? `Based on ${n} ${plural} — too few to read as a trend`
    : `Based on ${n} ${plural}`
}

/**
 * The sample size, and the lateness, in the hint — because the percentage on its own can mislead twice.
 *
 * A rate over two completions is not a rate, so the count it rests on is stated rather than implied. And a plant
 * at 80% needs to know whether the late fifth was a day late or a fortnight: `averageDaysLate` is averaged over
 * the late rounds only, so it answers "how bad is it when it goes wrong" instead of being diluted to nothing.
 */
function pmOnTimeHint(d) {
  if (!d) return undefined
  if (d.sampleSize === 0) {
    // Distinguish "no PM work at all" from "PM work that cannot be judged" — a logbook entry typed as
    // PREVENTIVE by hand never went through the schedule, so it carries no due date to measure against.
    return d.withoutDueDate > 0
      ? `${d.withoutDueDate} completed without a due date — cannot be judged`
      : 'No rounds completed in this period'
  }
  const parts = [`${d.onTime}/${d.sampleSize} rounds`]
  if (d.averageDaysLate != null) parts.push(`late by ${d.averageDaysLate}d on average`)
  if (d.withoutDueDate > 0) parts.push(`${d.withoutDueDate} unmeasurable`)
  return parts.join(' · ')
}

/**
 * Colour only once the figure means something.
 *
 * Below five measurable completions the percentage swings wildly — one late round in three is 67%, which would
 * paint a red tile over a plant that has barely started. Neutral until the sample can carry a judgement; the
 * hint still states the counts, so nothing is hidden.
 */
function pmOnTimeKind(d) {
  if (!d || d.onTimePct == null || d.sampleSize < 5) return undefined
  if (d.onTimePct >= 90) return 'success'
  if (d.onTimePct >= 70) return 'warning'
  return 'danger'
}

/** Tooltip styled from tokens, with tabular figures so hovering along a line does not shuffle the digits. */
function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div className="chart-tip">
      <strong>{label}</strong>
      {payload.map((p) => (
        <div key={p.name}>
          <span>{p.name}</span>
          <span className="tnum">{p.value}</span>
        </div>
      ))}
    </div>
  )
}
